/**
 * The bank account country for a driver-app Stripe Express account
 * (item 2, 2026-10-02). Warren: "home host Connect accounts take an
 * explicit US or Canada choice, the same pattern as location bank setup.
 * Never default."
 *
 * The rules the chooser (the earn walk's Get paid step and the
 * /my-account/connect/complete page) and every other payout entry point
 * decide on, kept pure so they are tested in one place:
 *   - nothing is preselected: a new account starts only once a country is
 *     chosen AND the host has confirmed it
 *   - an existing account never asks again; its country is shown
 *     read-only, because Stripe cannot change it
 *   - a home in one country and a payout account in the other is a
 *     warning, never a block (a host may live in one and own in the other)
 *   - an entry point that cannot ask (the charger page gate, Earnings,
 *     the carbon card) sends the host to the chooser instead of creating
 *     an account, unless the status says an account exists, and so does
 *     any caller the server answers with CONNECT_COUNTRY_REQUIRED
 *   - no account is created through an Api from before this change: it
 *     accepts `country` but ignores it and pins Canada, for good. Its
 *     status carries no `home_country`, which is how the app tells the
 *     two apart
 *   - after a create, the account's country is read back; a different
 *     one (an old Api task answered during a rolling deploy) stops the
 *     host before Stripe's checks
 */
import { homeCountryFromAny, type HomeCountry } from '@evi/shared';
import type { ConnectStatus } from './api';

/** Stripe Express countries the platform supports: the home countries. */
export type PayoutCountry = HomeCountry;
export const PAYOUT_COUNTRIES: readonly PayoutCountry[] = ['US', 'CA'];

/** The page that asks for the country, for every entry point that cannot. */
export const PAYOUT_CHOOSER_PATH = '/my-account/connect/complete';

/** Where the chooser's back button may return to: a charger page or its
 *  Earnings. Client only, never sent to the server (whose `next`
 *  allowlist is the earn walk alone), and checked again on read because
 *  the query string is user-editable. */
const CHOOSER_FROM_ALLOWED = /^\/my-chargers\/[a-f0-9]{24}(\/earnings)?$/;

/** The chooser path, remembering the screen it was opened from so its
 *  back button returns there instead of to Profile. */
export function payoutChooserPath(from?: string | null): string {
  return from && CHOOSER_FROM_ALLOWED.test(from)
    ? `${PAYOUT_CHOOSER_PATH}?from=${encodeURIComponent(from)}`
    : PAYOUT_CHOOSER_PATH;
}

/** The allowlisted `from` off the chooser's query string, or null. */
export function readChooserFrom(search: string): string | null {
  const from = new URLSearchParams(search).get('from');
  return from && CHOOSER_FROM_ALLOWED.test(from) ? from : null;
}

/** The server's refusal for a new account with no country. */
export const CONNECT_COUNTRY_REQUIRED = 'CONNECT_COUNTRY_REQUIRED';

export function isPayoutCountry(value: unknown): value is PayoutCountry {
  return value === 'US' || value === 'CA';
}

export function isCountryRequiredCode(code: string | null | undefined): boolean {
  return code === CONNECT_COUNTRY_REQUIRED;
}

/**
 * True when a failed call (a ky HTTPError) carries the server's
 * CONNECT_COUNTRY_REQUIRED code. False for any other code, a body that is
 * not JSON, or an error with no response (a network drop). Reads a clone,
 * so the caller can still read the body itself.
 */
export async function isCountryRequiredError(err: unknown): Promise<boolean> {
  const response = (err as { response?: unknown } | null)?.response;
  if (!(response instanceof Response)) return false;
  try {
    const body = await response.clone().json() as { error?: { code?: unknown } } | null;
    return typeof body?.error?.code === 'string' && isCountryRequiredCode(body.error.code);
  } catch {
    return false;
  }
}

/**
 * True when the status came from an Api that creates the account in the
 * chosen country. Since 2026-10-02 GET /me/connect/status always carries
 * `home_country` (null when unknown); an older Api omits it, and its
 * POST /me/connect/start ignores `country` and creates a Canadian
 * account, which Stripe can never change. So no create is offered until
 * the status says the Api is the new one (deploy the Api first).
 */
export function apiTakesCountry(status: object | null | undefined): boolean {
  return !!status && 'home_country' in status;
}

/** The country an API value names ('US', 'CA', 'Canada'), or null. */
export function payoutCountryFrom(value: string | null | undefined): PayoutCountry | null {
  return homeCountryFromAny(value);
}

/**
 * Whether "Set up payouts with Stripe" may be tapped. An existing account
 * resumes as it is. A new one needs a chosen country, the host's
 * confirmation that their bank account is there, and an Api that honours
 * the country (apiTakesCountry).
 */
export function canStartPayout(input: {
  hasAccount: boolean; country: string; confirmed: boolean; apiTakesCountry: boolean;
}): boolean {
  if (input.hasAccount) return true;
  return input.apiTakesCountry && isPayoutCountry(input.country) && input.confirmed;
}

/**
 * After a start that sent `country`: the account's country when the
 * status now shows an account in a DIFFERENT one, else null. Null too
 * when the status is unreadable or carries no country (nothing to check
 * against). A non-null answer means the host must not go on to Stripe.
 */
export function createdInOtherCountry(
  chosen: PayoutCountry,
  status: { has_account?: boolean; country?: string | null } | null | undefined,
): string | null {
  if (!status?.has_account || !status.country) return null;
  const actual = payoutCountryFrom(status.country);
  if (actual === chosen) return null;
  return actual ?? status.country;
}

/** True when both countries are known and differ (any case, codes or
 *  names). Unknown on either side is never a mismatch. */
export function countryMismatch(
  payout: string | null | undefined,
  home: string | null | undefined,
): boolean {
  const a = payoutCountryFrom(payout);
  const b = payoutCountryFrom(home);
  return a !== null && b !== null && a !== b;
}

/** Where an entry point that cannot ask for a country goes: straight to
 *  Stripe only when the status says an account exists (a resume never
 *  creates one). No account, or a status that could not be read, goes to
 *  the chooser, which reads the status itself: a blind start would let an
 *  Api from before 2026-10-02 create a Canadian account. */
export function payoutEntry(status: Pick<ConnectStatus, 'has_account'> | null | undefined): 'chooser' | 'start' {
  return status?.has_account === true ? 'start' : 'chooser';
}

/** connect.json key for a country's name on its own (a select option, a
 *  "Payout account country: X" value): 'country.us', 'country.ca'. */
export function payoutCountryLabelKey(country: PayoutCountry): string {
  return `country.${country.toLowerCase()}`;
}

/** connect.json key for a country's name inside a sentence ("in the
 *  United States"): 'country.inText.us', 'country.inText.ca'. */
export function payoutCountryTextKey(country: PayoutCountry): string {
  return `country.inText.${country.toLowerCase()}`;
}
